import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import {
  RATED_CATEGORIES,
  type TalentTeamRow,
} from './talentMapShared';
import { CategoryCurve } from './CategoryCurve';
import { buildCurvePoints } from './talentMapShared';
import { curveBucket, isOnLicense } from '../../../../../shared/talent-map-stats';

interface Props {
  rows: TalentTeamRow[];
}

/** Right panel of the "Mapa del equipo" tab: curva de distribución (doc 02/09),
 *  detalle por categoría, average confidence bar and computed insight cards. */
export function TeamDistributionPanel({ rows }: Props) {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    // Onboarding y licencia quedan FUERA de la distribución del ciclo (spec
    // §7.3 + Sol 18/09): no cuentan en el total, ni como slice, ni en los %.
    const eligibleRows = rows.filter(r => r.eligibilityStatus !== 'onboarding' && !isOnLicense(r));
    const total = eligibleRows.length;
    // Solo cuentan las categorías CONFIRMADAS: lo que el cron dejó en
    // auto_assigned no es un "Cumple" real (ver curveBucket).
    const byCategory = RATED_CATEGORIES.map(cat => ({
      cat,
      count: eligibleRows.filter(r => curveBucket(r) === cat).length,
    }));
    const rated = byCategory.reduce((acc, c) => acc + c.count, 0);
    const unrated = Math.max(0, total - rated);

    const confidences = eligibleRows
      .map(r => r.confidencePercent)
      .filter((c): c is number => c != null);
    const avgConfidence = confidences.length > 0
      ? Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length)
      : null;

    const onboardingCount = rows.filter(r => r.eligibilityStatus === 'onboarding').length;
    return { total, byCategory, unrated, avgConfidence, onboardingCount };
  }, [rows]);

  const insights = useMemo(() => {
    const total = stats.total;
    if (total === 0) return [];
    const count = (cat: string) => stats.byCategory.find(c => c.cat === cat)?.count ?? 0;
    const highPerf = count('top_performer') + count('performance_solida');
    const devCases = count('cumple_con_oportunidades') + count('low_performance');
    const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const hrbpRecent = rows.filter(r =>
      r.hrbpModifiedAt && new Date(r.hrbpModifiedAt).getTime() >= monthAgo,
    ).length;
    const pendingEvidence = rows.filter(r => r.processStatus === 'pending_evidence').length;

    const list: string[] = [];
    if (highPerf > 0) list.push(t('performance.talent_map.team_tab.insight_high_perf', { pct: Math.round((highPerf / total) * 100) }));
    if (devCases > 0) list.push(t('performance.talent_map.team_tab.insight_dev_cases', { count: devCases }));
    if (hrbpRecent > 0) list.push(t('performance.talent_map.team_tab.insight_hrbp_changes', { count: hrbpRecent }));
    if (list.length < 2 && pendingEvidence > 0) {
      list.push(t('performance.talent_map.team_tab.insight_pending_evidence', { count: pendingEvidence }));
    }
    return list.slice(0, 3);
  }, [rows, stats, t]);

  // Curva sobre TODAS las filas (incluye Onboarding y Sin completar como
  // puntos del eje, doc 02/09) — los % son sobre el universo visible, que
  // NO incluye licencias (fuera del ciclo).
  const points = buildCurvePoints(rows, t as any);
  const visible = rows.filter(r => !isOnLicense(r)).length;

  if (rows.length === 0) return null;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold mb-1">{t('performance.talent_map.team_tab.distribution')}</h3>
        <p className="text-xs text-muted-foreground mb-1">
          {visible} clappers
        </p>
        <CategoryCurve points={points} height={180} />
        <div className="space-y-1.5 mt-2">
          {points.map(p => (
            <div key={p.key} className="flex items-center gap-2 text-sm">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
              <span className={p.count > 0 ? 'text-foreground/90' : 'text-muted-foreground'}>
                {p.label} — {p.count} ({p.pct}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {stats.avgConfidence != null && (
        <div>
          <h3 className="font-semibold mb-2">{t('performance.talent_map.team_tab.avg_confidence')}</h3>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-foreground transition-all"
                style={{ width: `${stats.avgConfidence}%` }}
              />
            </div>
            <span className="text-sm font-semibold tabular-nums shrink-0">{stats.avgConfidence}%</span>
          </div>
        </div>
      )}

      {insights.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">{t('performance.talent_map.team_tab.insights')}</h3>
          <div className="space-y-2">
            {insights.map((text, i) => (
              <Card key={i} className="bg-muted/50 border-0 shadow-none">
                <CardContent className="p-3">
                  <p className="text-sm text-foreground/90">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
